import React, { useEffect, useRef, useState } from "react";
import { Drawer, useMediaQuery } from "@mui/material";
import Button from "../button/button.component";
import FormInput from "../form-input/form-input.component";
import { useDispatch, useSelector } from "react-redux";
import {
  setDeliveryAddress,
  setScheduledTime,
} from "../../store/cart/cart.action";
import { selectKitchen } from "../../store/kitchen/kitchen.selector";
import { getColor } from "../../utils/utils.common";
import {
  selectDeliveryAddress,
  selectScheduledTime,
} from "../../store/cart/cart.selector";
import OrderTypeButtonGroup from "../shared/buttonGroup/orderTypeButtonGroup.component";
import { CloseIcon, FieldErrorIcon, MarkerIcon } from "../icons";
import {
  Container,
  Header,
  HeaderText,
  IconContainer,
  OrderTypeButtonsContainer,
  BoxItem,
  AddressBox,
  MarkerIconContainer,
  AddressText,
  ButtonContainer,
  ContentBox,
  Separator,
} from "./order-type-bottom-sheet.styles";
import RadioButton from "../shared/RadioButton/RadioButton.component";
import Schedule from "./schedule.component";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { getKitchenTimezone } from "../../utils/timezone";
import {
  getEnabledOrderTypes,
  ORDER_TYPE_QUERY_PARAM,
  OrderTypeValue,
  resolveOrderType,
  TABLE_NUMBER_QUERY_PARAM,
} from "../../utils/orderType";
import { isDeliveryAddressSet } from "../../utils/deliveryAddress";
import DeliveryAddressAutocomplete from "../delivery-address-autocomplete/delivery-address-autocomplete.component";
import {
  getDeliveryUnavailableMessage,
  isDeliveryOrderingAvailable,
} from "../../utils/deliveryConfig";

const ORDER_TYPE_LABELS: Record<OrderTypeValue, string> = {
  takeaway: "Take Away",
  dinein: "Dine In",
  delivery: "Delivery",
};

const OrderTypeBottomSheet = ({
  isOpen,
  toggleBottomSheet,
  onlineOrdersConfig,
  setOrderTypeError,
  tableNumber,
  orderType,
}) => {
  const getScheduledTime = useSelector(selectScheduledTime);
  const deliveryAddress = useSelector(selectDeliveryAddress);
  const isAsapSelection =
    getScheduledTime === null || getScheduledTime === "ASAP";

  const [selectedOption, setSelectedOption] = useState(
    isAsapSelection ? "ASAP" : "schedule"
  );

  const searchParams = new URLSearchParams(window.location.search);
  const kitchen = useSelector(selectKitchen);

  const getOrderType =
    orderType ||
    resolveOrderType(searchParams, onlineOrdersConfig);

  const [currentOrderType, setCurrentOrderType] = useState(getOrderType);

  const [currentTableNumber, setCurrentTableNumber] = useState(
    tableNumber ? tableNumber : null
  );
  const [currentScheduledTime, setCurrentScheduledTime] = useState<any>(
    isAsapSelection ? null : getScheduledTime
  );

  const isDeliveryAvailable = isDeliveryOrderingAvailable(
    kitchen?.deliverySchedule,
    onlineOrdersConfig?.onlineDeliveryPaused,
    getKitchenTimezone(kitchen?.timezone)
  );
  const deliveryUnavailableMessage = getDeliveryUnavailableMessage(
    kitchen?.deliverySchedule,
    onlineOrdersConfig?.onlineDeliveryPaused,
    getKitchenTimezone(kitchen?.timezone)
  );

  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );
  const backgroundSecondary = getColor(
    "background-secondary",
    onlineOrdersConfig
  );
  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);
  const backgroundTertiary = getColor("background-tertiary", onlineOrdersConfig);
  const brandAccent = getColor("brand-accent", onlineOrdersConfig);
  const textBody = getColor("text-body", onlineOrdersConfig);

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const orderWaitTime = onlineOrdersConfig?.orderReadyTime
    ? Number(onlineOrdersConfig?.orderReadyTime)
    : 15;
  const deliveryMinimumTime = kitchen?.deliveryConfig?.deliveryMinimumTime
    ? Number(kitchen.deliveryConfig.deliveryMinimumTime)
    : 15;

  const dispatch = useDispatch();
  let isMobile = useMediaQuery("(max-width: 768px)");

  const enabledOrderTypes = getEnabledOrderTypes(onlineOrdersConfig);
  const orderTypeButtons = enabledOrderTypes.map((value) => ({
    value,
    label:
      enabledOrderTypes.length === 1
        ? `${ORDER_TYPE_LABELS[value]} Only`
        : ORDER_TYPE_LABELS[value],
  }));

  useEffect(() => {
    setCurrentOrderType(getOrderType);
  }, [getOrderType, isOpen]);

  const handleOptionChange = (event) => {
    if (event.target.value === "ASAP") {
      setCurrentScheduledTime(null);
    } else {
      setCurrentScheduledTime(null);
    }
    setSelectedOption(event.target.value);
  };

  const handleTableNumberChange = (event) => {
    const { value } = event.target;
    setCurrentTableNumber(value);
  };

  const checkIsDisabled = () => {
    if (currentOrderType === "dinein") {
      if (currentTableNumber === null || currentTableNumber === "") {
        return true;
      }
      return false;
    }

    if (currentOrderType === "takeaway") {
      if (selectedOption === "ASAP") {
        return false;
      }

      if (currentScheduledTime === null) {
        return true;
      }
      return false;
    }

    if (currentOrderType === "delivery") {
      if (!isDeliveryAvailable || !isDeliveryAddressSet(deliveryAddress)) {
        return true;
      }
      if (selectedOption === "schedule" && currentScheduledTime === null) {
        return true;
      }
      return false;
    }

    return false;
  };

  const getScheduledDateTime = (scheduledValue: any) => {
    if (!scheduledValue || !scheduledValue?.time) {
      return null;
    }

    // Build the instant in the venue's timezone so the stored Date maps to the
    // kitchen's wall-clock slot regardless of the customer's browser timezone.
    const timezone = getKitchenTimezone(kitchen?.timezone);
    const selectedDay = moment.tz(timezone).add(scheduledValue.dayOffset || 0, "days");
    const selectedDateTime = moment.tz(
      `${selectedDay.format("YYYY-MM-DD")} ${scheduledValue.time}`,
      "YYYY-MM-DD hh:mmA",
      timezone
    );

    if (!selectedDateTime.isValid()) {
      return null;
    }

    return selectedDateTime.toDate();
  };

  const updateOrderTypeInURL = (
    nextOrderType: OrderTypeValue,
    nextTableNumber?: string | null
  ) => {
    const params = new URLSearchParams(window.location.search);

    if (nextOrderType === "delivery") {
      params.set(ORDER_TYPE_QUERY_PARAM, "delivery");
      params.delete(TABLE_NUMBER_QUERY_PARAM);
    } else if (nextOrderType === "dinein") {
      params.delete(ORDER_TYPE_QUERY_PARAM);
      if (nextTableNumber) {
        params.set(TABLE_NUMBER_QUERY_PARAM, nextTableNumber);
      } else {
        params.set(TABLE_NUMBER_QUERY_PARAM, "");
      }
    } else {
      params.delete(ORDER_TYPE_QUERY_PARAM);
      params.delete(TABLE_NUMBER_QUERY_PARAM);
    }

    navigate(`${pathname}?${params.toString()}`);
  };

  const handleSubmit = () => {
    if (currentOrderType === "dinein") {
      updateOrderTypeInURL("dinein", currentTableNumber);
      dispatch(setDeliveryAddress(null));

      if (setOrderTypeError) {
        setOrderTypeError(false);
      }
    }

    if (currentOrderType === "takeaway") {
      updateOrderTypeInURL("takeaway");
      dispatch(setDeliveryAddress(null));
      if (selectedOption === "ASAP") {
        dispatch(setScheduledTime(null));
      } else {
        const scheduledDateTime = currentScheduledTime?.time
          ? getScheduledDateTime(currentScheduledTime)
          : currentScheduledTime;
        dispatch(setScheduledTime(scheduledDateTime));
      }
    }

    if (currentOrderType === "delivery") {
      updateOrderTypeInURL("delivery");
      if (selectedOption === "ASAP") {
        dispatch(setScheduledTime(null));
      } else {
        const scheduledDateTime = currentScheduledTime?.time
          ? getScheduledDateTime(currentScheduledTime)
          : currentScheduledTime;
        dispatch(setScheduledTime(scheduledDateTime));
      }
      if (setOrderTypeError) {
        setOrderTypeError(false);
      }
    }

    toggleBottomSheet();
  };

  const handleOrderTypeChange = (nextOrderType) => {
    setCurrentOrderType(nextOrderType);
  };

  const [dayOffset, setDayOffset] = useState(0);

  // Scheduled times are not transferable between order types (e.g. a pickup
  // time may fall outside delivery hours), so reset the selection whenever the
  // order type changes — including opening the sheet into a different type.
  const prevOrderTypeRef = useRef(currentOrderType);
  useEffect(() => {
    if (prevOrderTypeRef.current !== currentOrderType) {
      prevOrderTypeRef.current = currentOrderType;
      setSelectedOption("ASAP");
      setCurrentScheduledTime(null);
      setDayOffset(0);
    }
  }, [currentOrderType]);

  const contentRef = useRef(null);

  const isScheduleView =
    selectedOption === "schedule" &&
    (currentOrderType === "takeaway" || currentOrderType === "delivery");

  return (
    <Drawer
      transitionDuration={{ enter: isMobile ? 300 : 0, exit: isMobile ? 300 : 0 }}
      open={isOpen}
      onClose={toggleBottomSheet}
      anchor="bottom"
      keepMounted
      sx={{
        "& .MuiDrawer-paper": {
          background: headerModalBackground,
          position: !isMobile && "absolute",
          top: !isMobile && "50%",
          left: !isMobile && "50%",
          transform: !isMobile && "translate(-50%, -50%) !important",
          width: isMobile ? "100%" : "560px",
          maxWidth: "100%",
          boxSizing: "border-box",
          // Auto/fit-content so the sheet sizes to content immediately (no
          // delayed scrollHeight expand). Mobile schedule still fills the viewport.
          height: isScheduleView
            ? isMobile
              ? "100vh"
              : "fit-content"
            : "auto",
          minHeight: isMobile ? "390px" : "520px",
          maxHeight: isMobile ? "100vh" : "94vh",
          borderRadius: !isMobile && "8px",
          borderTopLeftRadius:
            selectedOption !== "schedule" && isMobile && "8px",
          borderTopRightRadius:
            selectedOption !== "schedule" && isMobile && "8px",
          overflow:
            currentOrderType === "delivery" ||
            selectedOption === "schedule"
              ? "auto"
              : "hidden",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Container ref={contentRef}>
        <ContentBox>
          <Header headerModalBackground={headerModalBackground}>
            <HeaderText brandAccent={brandAccent}>Order Type</HeaderText>
            <IconContainer
              brandTertiary={brandTertiary}
              headerModalBackground={headerModalBackground}
              onClick={toggleBottomSheet}
            >
              <CloseIcon size={isMobile ? "middle" : "large"} />
            </IconContainer>
          </Header>

          <BoxItem>
            {orderTypeButtons.length > 0 && (
              <OrderTypeButtonsContainer>
                <OrderTypeButtonGroup
                  orderTypes={orderTypeButtons}
                  currentOrderType={currentOrderType}
                  onOrderTypeChange={
                    orderTypeButtons.length > 1
                      ? handleOrderTypeChange
                      : undefined
                  }
                />
              </OrderTypeButtonsContainer>
            )}
            {(currentOrderType === "takeaway" ||
              currentOrderType === "dinein") && (
              <AddressBox textBody={textBody}>
                <MarkerIconContainer>
                  <MarkerIcon />
                </MarkerIconContainer>
                <AddressText>{kitchen?.fullAddress}</AddressText>
              </AddressBox>
            )}

            {currentOrderType === "delivery" && !isDeliveryAvailable && (
              <AddressBox textBody={textBody}>
                <MarkerIconContainer>
                  <FieldErrorIcon size="middle" />
                </MarkerIconContainer>
                <AddressText>{deliveryUnavailableMessage}</AddressText>
              </AddressBox>
            )}
            {currentOrderType === "delivery" && isDeliveryAvailable && (
              <DeliveryAddressAutocomplete
                key="delivery-address-autocomplete"
                active={isOpen}
              />
            )}
            {currentOrderType === "delivery" && isDeliveryAvailable && (
              <div>
                <Separator backgroundTertiary={backgroundTertiary} />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ marginBottom: "20px", display: "flex" }}>
                    <RadioButton
                      name="asap"
                      value="ASAP"
                      label={`ASAP (Delivered in ${deliveryMinimumTime} minutes)`}
                      checked={selectedOption === "ASAP"}
                      onChange={handleOptionChange}
                    />
                  </div>
                  <div style={{ display: "flex" }}>
                    <RadioButton
                      name="schedule"
                      value="schedule"
                      label="Schedule For Later"
                      checked={selectedOption === "schedule"}
                      onChange={handleOptionChange}
                    />
                  </div>
                </div>

                {selectedOption === "schedule" && (
                  <Schedule
                    currentScheduledTime={currentScheduledTime}
                    setCurrentScheduledTime={setCurrentScheduledTime}
                    dayOffset={dayOffset}
                    setDayOffset={setDayOffset}
                    schedule={kitchen?.deliverySchedule}
                    minimumTime={kitchen?.deliveryConfig?.deliveryMinimumTime}
                  />
                )}
              </div>
            )}

            {currentOrderType === "takeaway" && (
              <div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ marginBottom: "20px", display: "flex" }}>
                    <RadioButton
                      name="asap"
                      value="ASAP"
                      label={`ASAP (Ready in ${orderWaitTime} minutes)`}
                      checked={selectedOption === "ASAP"}
                      onChange={handleOptionChange}
                    />
                  </div>
                  <div style={{ display: "flex" }}>
                    <RadioButton
                      name="schedule"
                      value="schedule"
                      label="Schedule For Later"
                      checked={selectedOption === "schedule"}
                      onChange={handleOptionChange}
                    />
                  </div>
                </div>

                {selectedOption === "schedule" && (
                  <Schedule
                    currentScheduledTime={currentScheduledTime}
                    setCurrentScheduledTime={setCurrentScheduledTime}
                    dayOffset={dayOffset}
                    setDayOffset={setDayOffset}
                  />
                )}
              </div>
            )}
            {currentOrderType === "dinein" && (
              <FormInput
                label="Table Number"
                placeholder="Enter table number"
                type="string"
                required
                onChange={handleTableNumberChange}
                name="tableNumber"
                value={currentTableNumber}
                autoFocusEnable
              />
            )}
          </BoxItem>
        </ContentBox>

        <ButtonContainer
          headerModalBackground={headerModalBackground}
          backgroundSecondary={backgroundSecondary}
        >
          <Button disabled={checkIsDisabled()} onClick={handleSubmit}>
            Confirm
          </Button>
        </ButtonContainer>
      </Container>
    </Drawer>
  );
};

export default OrderTypeBottomSheet;
